/* global axios */
import ApiClient from './ApiClient';

class FranchiseAPI extends ApiClient {
  constructor() {
    super('franchises', { accountScoped: true });
  }

  get(page = 1, status = '') {
    return axios.get(this.url, { params: { page, ...(status && { status }) } });
  }

  search(search = '', page = 1, status = '') {
    return axios.get(`${this.url}/search`, {
      params: { page, q: search, ...(status && { status }) },
    });
  }

  filter(page = 1, queryPayload = {}) {
    return axios.post(`${this.url}/filter?page=${page}`, { ...queryPayload });
  }

  filterCount(queryPayload = {}) {
    return axios.post(`${this.url}/filter_count`, { ...queryPayload });
  }

  suspend(franchiseId, suspensionReason) {
    return axios.post(`${this.url}/${franchiseId}/suspend`, {
      suspension_reason: suspensionReason,
    });
  }

  reactivate(franchiseId) {
    return axios.post(`${this.url}/${franchiseId}/reactivate`);
  }

  // Status of the franchise the logged-in user belongs to, if any.
  myMembership() {
    return axios.get(`${this.url}/my_membership`);
  }

  // Units
  getUnits(franchiseId) {
    return axios.get(`${this.url}/${franchiseId}/units`);
  }

  // Units of every franchise the logged-in user can reach.
  getAllUnits() {
    return axios.get(`${this.url}/units`);
  }

  createUnit(franchiseId, payload) {
    return axios.post(`${this.url}/${franchiseId}/units`, payload);
  }

  updateUnit(franchiseId, unitId, payload) {
    return axios.patch(`${this.url}/${franchiseId}/units/${unitId}`, payload);
  }

  deleteUnit(franchiseId, unitId) {
    return axios.delete(`${this.url}/${franchiseId}/units/${unitId}`);
  }

  // Attendants
  getAttendants(franchiseId) {
    return axios.get(`${this.url}/${franchiseId}/attendants`);
  }

  createAttendant(franchiseId, payload) {
    return axios.post(`${this.url}/${franchiseId}/attendants`, payload);
  }

  // Chaveado por user_id, e nao por id de linha: quem atende a duas unidades tem duas linhas,
  // e o id delas muda a cada edicao. O recurso e a participacao da pessoa.
  updateAttendant(franchiseId, userId, payload) {
    return axios.patch(
      `${this.url}/${franchiseId}/attendants/${userId}`,
      payload
    );
  }

  deleteAttendant(franchiseId, userId) {
    return axios.delete(`${this.url}/${franchiseId}/attendants/${userId}`);
  }

  // Billing
  billingOverview(params = {}) {
    return axios.get(`${this.url}/billing/overview`, { params });
  }

  billingDetail(franchiseId, params = {}) {
    return axios.get(`${this.url}/${franchiseId}/billing`, { params });
  }

  billingExport(params = {}) {
    return axios.get(`${this.url}/billing/export`, {
      params,
      responseType: 'blob',
    });
  }

  createCharge(franchiseId, payload) {
    return axios.post(`${this.url}/${franchiseId}/billing/charges`, {
      billing_charge: payload,
    });
  }

  createSubscription(franchiseId, payload) {
    return axios.post(`${this.url}/${franchiseId}/billing/subscription`, {
      billing_subscription: payload,
    });
  }

  updateSubscription(franchiseId, payload) {
    return axios.patch(`${this.url}/${franchiseId}/billing/subscription`, {
      billing_subscription: payload,
    });
  }

  cancelSubscription(franchiseId) {
    return axios.delete(`${this.url}/${franchiseId}/billing/subscription`);
  }

  // Inboxes
  getInboxes(franchiseId) {
    return axios.get(`${this.url}/${franchiseId}/inboxes`);
  }

  // Unassigned inboxes plus the ones already on this franchise.
  getAvailableInboxes(franchiseId) {
    return axios.get(`${this.url}/${franchiseId}/inboxes/available`);
  }

  assignInbox(franchiseId, inboxId) {
    return axios.patch(`${this.url}/${franchiseId}/inboxes/${inboxId}`);
  }

  unassignInbox(franchiseId, inboxId) {
    return axios.delete(`${this.url}/${franchiseId}/inboxes/${inboxId}`);
  }

  // Payment methods
  getPaymentMethods(franchiseId) {
    return axios.get(`${this.url}/${franchiseId}/payment_methods`);
  }

  // The raw card never touches our database — Asaas returns a token and we keep that.
  tokenizeCreditCard(franchiseId, payload) {
    return axios.post(
      `${this.url}/${franchiseId}/payment_methods/tokenize_credit_card`,
      payload
    );
  }

  setDefaultPaymentMethod(franchiseId, paymentMethodId) {
    return axios.post(
      `${this.url}/${franchiseId}/payment_methods/${paymentMethodId}/set_default`
    );
  }

  deletePaymentMethod(franchiseId, paymentMethodId) {
    return axios.delete(
      `${this.url}/${franchiseId}/payment_methods/${paymentMethodId}`
    );
  }
}

export default new FranchiseAPI();
